import { z } from 'zod';

export const groupIdSchema = z.enum([
  'blue',
  'green',
  'red',
  'yellow',
  'orange',
  'pink',
  'lightblue',
  'brown',
  'mint',
  'black',
]);
export type GroupId = z.infer<typeof groupIdSchema>;

export const upgradeKindSchema = z.enum(['grocery', 'residential']);
export type UpgradeKind = z.infer<typeof upgradeKindSchema>;

const hexColor = z.string().regex(/^#[0-9a-f]{6}$/, 'expected a lowercase #rrggbb color');

export const groupSchema = z.object({
  id: groupIdSchema,
  label: z.string().min(1),
  color: hexColor,
  onColor: hexColor,
  /** Non-color affordance so groups stay distinguishable without hue. */
  pattern: z.enum([
    'solid',
    'diagonal',
    'dots',
    'grid',
    'chevron',
    'wave',
    'stripe',
    'crosshatch',
    'check',
    'bar',
  ]),
  size: z.number().int().min(2).max(4),
  rent: z.array(z.number().int().min(1)).min(2).max(4),
});
export type PropertyGroup = z.infer<typeof groupSchema>;

export const groupsFileSchema = z.object({ groups: z.array(groupSchema).min(1) });

export const artworkSchema = z.object({
  figmaNode: z.string().regex(/^\d+:\d+$/),
  alt: z.string().min(8),
});

/**
 * Declarative effect vocabulary. The interpreter supports exactly these verbs;
 * card content may not introduce new behavior.
 */
export const effectSchema = z.union([
  z.object({ drawCards: z.object({ player: z.literal('actor'), count: z.number().int().min(1) }) }),
  z.object({
    createDebt: z.object({
      debtor: z.enum(['target', 'allOthers']),
      creditor: z.literal('actor'),
      amount: z.number().int().min(1),
    }),
  }),
  z.object({
    chargeRent: z.object({
      debtor: z.enum(['target', 'allOthers']),
      creditor: z.literal('actor'),
      group: z.literal('target'),
    }),
  }),
  z.object({ takeProperty: z.object({ from: z.literal('target'), to: z.literal('actor') }) }),
  z.object({
    swapProperty: z.object({
      actorProperty: z.literal('ownSelection'),
      targetProperty: z.literal('target'),
    }),
  }),
  z.object({
    transferCompleteMall: z.object({ from: z.literal('target'), to: z.literal('actor') }),
  }),
  z.object({
    placeUpgrade: z.object({
      upgrade: upgradeKindSchema,
      rentBonus: z.number().int().min(1),
    }),
  }),
  z.object({ cancelAction: z.object({ scope: z.literal('self') }) }),
  z.object({ doubleRent: z.object({}) }),
]);
export type Effect = z.infer<typeof effectSchema>;

export const targetSchema = z.object({
  kind: z.enum([
    'none',
    'player',
    'property',
    'propertySwap',
    'completeMall',
    'rentGroup',
    'rentGroupAndPlayer',
  ]),
  scope: z.enum(['opponents', 'own']).optional(),
  count: z.number().int().min(1).optional(),
  excludeCompleteMalls: z.boolean().optional(),
  requiresUpgrade: upgradeKindSchema.optional(),
});
export type TargetSpec = z.infer<typeof targetSchema>;

export const playSchema = z.object({
  target: targetSchema,
  effects: z.array(effectSchema).min(1),
  /** Whether affected players get a Blame the Investment Committee window. */
  cancellable: z.boolean().default(false),
  /** Playable only as a response inside a cancellation window. */
  reactionOnly: z.boolean().default(false),
  /** Playable only attached to a rent card in the same action. */
  rentModifierOnly: z.boolean().default(false),
  /** Marks the card as a mall upgrade rather than a one-shot action. */
  upgrade: upgradeKindSchema.optional(),
});
export type PlaySpec = z.infer<typeof playSchema>;

const baseCard = {
  id: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'card ids are lowercase kebab-case'),
  title: z.string().min(1),
  copies: z.number().int().min(1),
  value: z.number().int().min(0),
  artwork: artworkSchema,
  description: z.string().optional(),
};

export const moneyCardSchema = z.object({
  ...baseCard,
  kind: z.literal('money'),
  value: z.number().int().min(1),
});

export const propertyCardSchema = z.object({
  ...baseCard,
  kind: z.literal('property'),
  group: groupIdSchema,
});

export const propertyWildcardSchema = z.object({
  ...baseCard,
  kind: z.literal('propertyWildcard'),
  groups: z.array(groupIdSchema).min(2),
});

export const actionCardSchema = z.object({
  ...baseCard,
  kind: z.literal('action'),
  description: z.string().min(1),
  play: playSchema,
});

export const rentCardSchema = z.object({
  ...baseCard,
  kind: z.literal('rent'),
  description: z.string().min(1),
  groups: z.array(groupIdSchema).min(2),
  audience: z.enum(['allOthers', 'onePlayer']),
  play: playSchema,
});

export const cardSchema = z.discriminatedUnion('kind', [
  moneyCardSchema,
  propertyCardSchema,
  propertyWildcardSchema,
  actionCardSchema,
  rentCardSchema,
]);
export type CardDefinition = z.infer<typeof cardSchema>;
export type MoneyCard = z.infer<typeof moneyCardSchema>;
export type PropertyCard = z.infer<typeof propertyCardSchema>;
export type PropertyWildcard = z.infer<typeof propertyWildcardSchema>;
export type ActionCard = z.infer<typeof actionCardSchema>;
export type RentCard = z.infer<typeof rentCardSchema>;

export const cardsFileSchema = z.object({ cards: z.array(cardSchema).min(1) });

export const rulesSchema = z.object({
  players: z.object({
    minimum: z.number().int().min(2),
    maximum: z.number().int().max(5),
  }),
  startingHand: z.number().int().min(1),
  turnDraw: z.number().int().min(1),
  emptyHandDraw: z.number().int().min(1),
  actionsPerTurn: z.number().int().min(1),
  endTurnHandLimit: z.number().int().min(1),
  completeGroupsToWin: z.number().int().min(1),
  requireDistinctGroups: z.boolean(),
  reshuffleDiscardWhenDrawPileEmpty: z.boolean(),
});
export type Rules = z.infer<typeof rulesSchema>;

/** Invented strip-mall tenants offered when naming a wildcard. Flavor only. */
export const tenantsFileSchema = z.object({
  tenants: z.array(z.string().min(2).max(40)).min(10),
});

export const figmaManifestSchema = z.object({
  file: z.string().min(1),
  nodes: z
    .array(
      z.object({
        node: z.string().regex(/^\d+:\d+$/),
        name: z.string().min(1),
        covers: z.string().min(1),
      }),
    )
    .min(1),
});
export type FigmaManifest = z.infer<typeof figmaManifestSchema>;

/** The deck total the printed game ships with. Content must expand to exactly this. */
export const EXPECTED_DECK_SIZE = 106;

export const EXPECTED_CATEGORY_TOTALS: Record<CardDefinition['kind'], number> = {
  action: 34,
  rent: 13,
  property: 28,
  propertyWildcard: 11,
  money: 20,
};
